import React, { useEffect, useRef, useState } from "react";

const MAX_FILE_SIZE = 3.75 * 1024 * 1024; // 3.75 MB
const MAX_DIMENSION = 8000;               // 8,000 px
const ACCEPTED_TYPES = ["image/png", "image/jpeg"];

type Props = {
  onFile?: (file: File | null) => void;
  onUploadComplete?: (done: boolean) => void;
  placeholder?: string;
};

const readAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject();
    reader.readAsDataURL(file);
  });

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise(async (resolve, reject) => {
    try {
      const dataUrl = await readAsDataURL(file);
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject();
      img.src = dataUrl;
    } catch {
      reject();
    }
  });

const FileUpload: React.FC<Props> = ({ onFile, onUploadComplete, placeholder = "Upload architecture diagram" }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = async (f: File): Promise<string | null> => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return "Only PNG and JPEG files are accepted.";
    }
    if (f.size > MAX_FILE_SIZE) {
      return `File size exceeds 3.75 MB (${(f.size / (1024 * 1024)).toFixed(2)} MB).`;
    }
    try {
      const { width, height } = await getImageDimensions(f);
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        return `Image dimensions exceed 8,000 × 8,000 px (${width} × ${height} px).`;
      }
    } catch {
      return "Could not read image dimensions.";
    }
    return null;
  };

  const processFile = async (f: File) => {
    setError(null);
    const err = await validate(f);
    if (err) {
      setError(err);
      // reset input so the same file can be re-selected after fixing
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(f);
    onFile && onFile(f);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    processFile(f);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    processFile(f);
  };

  useEffect(() => {
    if (!file) return;
    let id: number;

    readAsDataURL(file).then((dataUrl) => {
      setPreview(dataUrl);
      setProgress(0);
      setUploading(true);
      onUploadComplete && onUploadComplete(false);

      id = window.setInterval(() => {
        setProgress((p) => {
          const next = Math.min(100, p + Math.floor(Math.random() * 20) + 10);
          if (next >= 100) {
            window.clearInterval(id);
            setUploading(false);
            onUploadComplete && onUploadComplete(true);
          }
          return next;
        });
      }, 300);
    });

    return () => {
      window.clearInterval(id);
    };
  }, [file]);

  const handleDelete = () => {
    setFile(null);
    setPreview(null);
    setProgress(0);
    setUploading(false);
    setError(null);
    onFile && onFile(null);
    onUploadComplete && onUploadComplete(false);
  };

  const openPreview = () => {
    if (!preview) return;
    window.open(preview, "_blank");
  };

  const uploaded = !uploading && progress === 100;

  return (
    <div className="space-y-2">
      {!file ? (
        <>
          <div
            className={`border-2 border-dashed rounded-xl px-4 py-5 text-center cursor-pointer transition select-none ${
              error
                ? "border-red-400 bg-red-50"
                : isDragging
                ? "border-purple-600 bg-purple-100"
                : "border-purple-400 hover:bg-purple-50"
            }`}
            onClick={() => inputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleChange}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-2 pointer-events-none">
              {error ? (
                <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              ) : (
                <svg className="w-10 h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12V4m0 0l-3 3m3-3l3 3" />
                </svg>
              )}
              <div className={`text-sm font-medium ${error ? "text-red-500" : "text-purple-600"}`}>
                {error ? "Try again" : placeholder}
              </div>
              <div className="text-xs text-gray-400">or drag and drop here</div>
               <div className="text-xs text-gray-400">File size should not exceed 3.75 MB</div>
            </div>
          </div>

          {/* Validation error message */}
          {error && (
            <div className="flex items-start gap-1.5 text-xs text-red-600 px-1">
              <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}
        </>
      ) : (
        <div className="border rounded-xl p-3 space-y-3">
          <div className="flex items-center gap-3">
            {/* Thumbnail */}
            <div
              className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-purple-50 border border-purple-100 flex items-center justify-center cursor-pointer"
             
            >
             {preview && (
  <img
    src={preview}
    alt={file?.name || "preview-image"}
    className="w-full h-full object-cover"
    onClick={openPreview}
  />
)}
              {uploaded && (
                <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="text-sm font-medium text-gray-800 truncate">{file.name}</div>
                {uploaded && (
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {file.size < 1024 * 1024
                  ? `${(file.size / 1024).toFixed(2)} KB`
                  : `${(file.size / (1024 * 1024)).toFixed(2)} MB`}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {new Date(file.lastModified).toISOString().slice(0, 19)}
              </div>
            </div>

            {/* Delete button */}
            <button onClick={handleDelete} className="flex-shrink-0 px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm">
              Delete
            </button>
          </div>

          {/* Progress bar */}
          <div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${uploaded ? "bg-green-500" : "bg-purple-500"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className={`text-xs mt-1 ${uploaded ? "text-green-600 font-medium" : "text-gray-500"}`}>
              {uploading ? `Uploading... ${progress}%` : "Uploaded successfully"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
